import {
  VesselType,
  type VesselProfile,
  buildVesselProfile,
} from '@deckee/utilities';

/**
 * Resolves the watercraft the user has chosen into the profile the rating
 * engine needs.
 *
 * Two sources describe a watercraft type in this app and they do not share a
 * spelling: the saved vessel record uses the API enum (`paddle-craft`), while
 * the manual picker uses its own display values (`paddle craft`). Both are
 * normalised here so the rest of the forecast never has to care which one it
 * got.
 */

/** Used when the member has told us nothing about their boat. */
export const DEFAULT_VESSEL_TYPE = VesselType.Motorboat;
export const DEFAULT_VESSEL_LENGTH_M = 4.5;

const VESSEL_TYPE_BY_NORMALISED_NAME: Readonly<Record<string, VesselType>> = {
  motorboat: VesselType.Motorboat,
  motorboats: VesselType.Motorboat,
  sailboat: VesselType.Sailboat,
  sailingboat: VesselType.Sailboat,
  yacht: VesselType.Sailboat,
  personalwatercraft: VesselType.Pwc,
  pwc: VesselType.Pwc,
  jetski: VesselType.Pwc,
  paddlecraft: VesselType.Paddle,
  paddle: VesselType.Paddle,
  kayak: VesselType.Paddle,
  canoe: VesselType.Paddle,
};

/** Strips spacing, hyphens and casing so both spellings resolve to one key. */
const normaliseTypeName = (value: string): string =>
  value.toLowerCase().replace(/[^a-z]/g, '');

export const resolveVesselType = (
  watercraftType: string | null | undefined,
): VesselType => {
  if (!watercraftType) {
    return DEFAULT_VESSEL_TYPE;
  }

  return (
    VESSEL_TYPE_BY_NORMALISED_NAME[normaliseTypeName(watercraftType)] ??
    DEFAULT_VESSEL_TYPE
  );
};

/**
 * Build the rating profile from whatever the app knows about the member's
 * watercraft. Falls back to the default small motorboat rather than refusing to
 * rate, so a member who has never set a boat still gets a forecast.
 */
export const resolveVesselProfile = (
  watercraftType: string | null | undefined,
  vesselLengthM: number | null | undefined,
): VesselProfile =>
  buildVesselProfile(
    resolveVesselType(watercraftType),
    vesselLengthM != null &&
      Number.isFinite(vesselLengthM) &&
      vesselLengthM >= 1
      ? vesselLengthM
      : DEFAULT_VESSEL_LENGTH_M,
  );
